/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import axios from 'axios';
import PropTypes from 'prop-types';
import get from 'lodash/get';

import type {
    AsyncActionStepContext,
    AsyncDispatch,
    ActionStepFn,
    AsyncState,
    AsyncAction,
    AsyncReducerFn,
} from '../../types/async';
import type { SecurityUtils, RequestOptions } from '../../types/api';

/*
  Async helper functions to reduce the amount of code to write to implement actions
  and reducers that do asynchronous work.

  All async data is combined with loading and loaded tracking, error handling.
 */

// Builds a PropType.shape the async action and reducers use
export function asyncProps(propType: PropTypes.Requireable<unknown>) {
    return PropTypes.shape({
        loading: PropTypes.bool,
        loaded: PropTypes.bool,
        error: PropTypes.string,
        data: propType,
    });
}

// Builds an object that represents an async helper object containing initial state data
export function asyncDefaults(data: unknown) {
    return {
        loading: false,
        loaded: false,
        error: null,
        data,
    };
}

// Default action step functions
// Function signature (dispatch, { getState, type, key }) => { return <action> }
const startActionFn = (dispatch: AsyncDispatch, { type }: AsyncActionStepContext) =>
    dispatch({ type });
// Function signature (dispatch, { getState, payload, type, key }) => { return <action> }
const successActionFn = (
    dispatch: AsyncDispatch,
    { payload, type }: AsyncActionStepContext
) => dispatch({ type, payload });
// Function signature (dispatch, { getState, error, message, type, key }) => { return <action> }
const failureActionFn = (
    dispatch: AsyncDispatch,
    { message, type }: AsyncActionStepContext
) => dispatch({ type, error: message });

// Wraps an asynchronous task with distinct reducer steps. Should be paired with makeAsyncReducer
export function makeAsyncAction(
    type: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    thunkCallback: (...args: any[]) => () => Promise<{ data: Record<string, unknown> }>,
    key?: string,
    {
        start = startActionFn,
        success = successActionFn,
        failure = failureActionFn,
    }: {
        start?: ActionStepFn;
        success?: ActionStepFn;
        failure?: ActionStepFn;
    } = {}
) {
    return (...args: unknown[]) => {
        const thunk = thunkCallback(...args);

        return async (
            dispatch: AsyncDispatch,
            getState: () => Record<string, unknown>
        ) => {
            start(dispatch, { getState, type: `${type}_START`, key });

            try {
                const response = await thunk();
                const payload = (
                    key !== undefined ? get(response, `data.${key}`) : response.data
                ) as Record<string, unknown> | undefined;

                if (payload === undefined) {
                    throw new Error(`Did not receive data from the server.`);
                }

                // Handle HTTP 200 validation errors as HTTP 400 errors.
                if (payload.status === 'ValidationError') {
                    // eslint-disable-next-line no-throw-literal
                    throw {
                        response: {
                            ...response,
                            status: 405,
                            data: payload,
                        },
                    };
                }

                success(dispatch, { getState, payload, type: `${type}_SUCCESS`, key });

                return payload;
            } catch (error: unknown) {
                const axiosError = error as {
                    response?: {
                        status: number;
                        data?: {
                            error?: string;
                            err?: string;
                            errors?: string[];
                            transaction_id?: string;
                        };
                    };
                    request?: unknown;
                    message?: string;
                };
                let message;

                if (axiosError.response) {
                    // Handle 5xx errors
                    const response = axiosError.response;
                    if (
                        response.data &&
                        response.data.error &&
                        !response.data.err &&
                        !response.data.transaction_id
                    ) {
                        response.data.err = response.data.error;
                    }

                    if (response.status >= 500) {
                        switch (response.status) {
                            case 500: // Internal Server Error
                                if (response.data && response.data.err) {
                                    message = response.data.err;
                                } else {
                                    message =
                                        'The server encountered an error processing the request. Try again later.';
                                }
                                break;
                            case 501: // Not Implemented
                                if (response.data && response.data.err) {
                                    message = response.data.err;
                                } else {
                                    message =
                                        'The server does not know how to respond to that request.';
                                }
                                break;
                            case 502: // Bad Gateway
                            case 503: // Service Unavailable
                            case 504: // Gateway Timeout
                                if (response.data && response.data.err) {
                                    message = response.data.err;
                                } else {
                                    message =
                                        'The server could not be reached or is not responding. Try again later.';
                                }
                                break;
                            default:
                                if (response.data && response.data.err) {
                                    message = response.data.err;
                                } else {
                                    message =
                                        'Something went wrong while talking with the server. Try again later.';
                                }
                        }
                    } else if (response.status >= 400) {
                        switch (response.status) {
                            case 401: // Unauthorized (not authenticated)
                                if (response.data && response.data.err) {
                                    message = response.data.err;
                                } else {
                                    message = 'You must be logged in to do that action.';
                                }
                                break;
                            case 403: // Forbidden (not authorized)
                                if (response.data && response.data.err) {
                                    message = response.data.err;
                                } else {
                                    message =
                                        'Your permissions do not permit you to do that action.';
                                }
                                break;
                            case 404: // Not Found
                                if (response.data && response.data.err) {
                                    message = response.data.err;
                                } else {
                                    message = 'No data for that resource was found.';
                                }
                                break;
                            case 405: // Validation Error
                                if (response.data && response.data.err) {
                                    message = response.data.err;
                                    if (
                                        response.data.errors &&
                                        response.data.errors.length
                                    ) {
                                        message = response.data.errors.sort().join('\n');
                                    }
                                } else {
                                    message =
                                        'There are errors in your request. Please check your inputs and try again.';
                                }
                                break;
                            case 422: // Unprocessable Entity
                                if (response.data && response.data.err) {
                                    message = response.data.err;
                                } else {
                                    message =
                                        'There are errors in your request, check your inputs and try again.';
                                }
                                break;
                            default:
                                if (response.data && response.data.err) {
                                    message = response.data.err;
                                } else {
                                    message =
                                        'A problem with your request has occurred. Try again later.';
                                }
                        }
                    } else if (response.data && response.data.err) {
                        message = response.data.err;
                    } else {
                        message =
                            'A temporary problem has caused your request to fail. Try again later.';
                    }
                }
                // Error with the request (no server response)
                else if (axiosError.request) {
                    message = 'Something went wrong while talking to the server';
                }
                // Raw Axios or other error describing the problem (technical) || Unknown failure
                else {
                    message = axiosError.message || 'Something went wrong unexpectedly';
                }

                failure(dispatch, {
                    getState,
                    error,
                    message,
                    type: `${type}_FAILURE`,
                    key,
                });

                // Can be caught with try {} catch (error) , or is using Promise syntax with .catch()
                throw new Error(message);
            }
        };
    };
}

//Creates a simple action to reset the async helper state back to initial data
export function makeAsyncResetAction(type: string) {
    return () => (dispatch: AsyncDispatch) => dispatch({ type });
}

// Common async reducer functions, a success which will populate data coming from the server on success but
// allows replacement an example: success can be replaced in makeAsyncReducer to instead reset to the initial
// data on a delete instead.
// Function signatures (state, action, initial) => { return <state> }

const startReducerFn: AsyncReducerFn = (state) => ({
    loading: true,
    loaded: false,
    error: null,
    data: state.data,
});
const successReducerFn: AsyncReducerFn = (state, action) => ({
    loading: false,
    loaded: true,
    error: null,
    data: action.payload,
});
const failureReducerFn: AsyncReducerFn = (state, action) => ({
    loading: false,
    loaded: true,
    error: action.error ?? null,
    data: state.data,
});

// Creates a reducer to handle all makeAsyncAction action steps
export function makeAsyncReducer(
    type: string,
    {
        initialData = null,
        start = startReducerFn,
        success = successReducerFn,
        failure = failureReducerFn,
    }: {
        initialData?: unknown;
        start?: AsyncReducerFn;
        success?: AsyncReducerFn;
        failure?: AsyncReducerFn;
    } = {}
) {
    const initial: AsyncState = {
        loading: false,
        loaded: false,
        error: null,
        data: initialData,
    };

    return (state = initial, action: AsyncAction) => {
        switch (action.type) {
            case `${type}_START`:
                return start(state, action, initialData);
            case `${type}_SUCCESS`:
                return success(state, action, initialData);
            case `${type}_FAILURE`:
                return failure(state, action, initialData);
            default:
                return state;
        }
    };
}

/**
 * Creates a simple reducer to reset the async helper state back to initial data
 * @param {string} type - action type
 * @param {*} [initialData=null]
 * @return {function(state, action): *} redux reducer function
 */
export function makeAsyncResetReducer(type: string, initialData: unknown = null) {
    return (state: AsyncState, action: AsyncAction) => {
        if (action.type === type) {
            return asyncDefaults(initialData);
        }

        return state;
    };
}

/**
 * Gates all related reducers behind a prefix check and returns a reducing reducer
 * @param {*} initialState - initial async state to use for all reducers
 * @param {string} prefix - action prefix
 * @param {...Function} reducers
 * @return {function(state, action): *} redux reducer function
 */
export function mergeReducers(
    initialState: AsyncState,
    prefix: string,
    ...reducers: ((state: AsyncState, action: AsyncAction) => AsyncState)[]
) {
    return (initial = initialState, action: AsyncAction) => {
        if (typeof action.type === 'undefined' || !action.type.startsWith(prefix)) {
            return initial;
        }

        return reducers.reduce((state, reducer) => reducer(state, action), initial);
    };
}

export const STATE_UNKNOWN = -1;
export const STATE_UNLOADED = 0;
export const STATE_LOADING = 1;
export const STATE_READY = 2;
export const STATE_ERROR = 3;

/**
 * Makes some async selector helpers for $reducer
 *
 * isLoading() Returns an object of booleans. Any and All are included for quick checks across all keys
 * isLoaded() Returns an object of booleans. Any and All are included for quick checks across all keys
 * hasErrors() Returns an object of booleans. Any and All are included for quick checks across all keys
 * getErrors() Returns an object of error messages for only the keys that have errors.
 *
 * @param {string} reducer - name of UI reducer found on the root state object
 * @return {Object} object of selector functions
 */
export function makeAsyncSelectors(reducer: string) {
    return {
        isLoading(state: Record<string, Record<string, AsyncState>>, ...keys: string[]) {
            const result: Record<string, boolean> = {};
            let all = true;
            let any = false;

            keys.forEach((key) => {
                all =
                    all &&
                    state[reducer] &&
                    state[reducer][key] &&
                    state[reducer][key].loading;
                any =
                    any ||
                    (state[reducer] &&
                        state[reducer][key] &&
                        state[reducer][key].loading);
                result[key] =
                    state[reducer] && state[reducer][key] && state[reducer][key].loading;
            });

            result.all = all;
            result.any = any;

            return result;
        },

        isLoaded(state: Record<string, Record<string, AsyncState>>, ...keys: string[]) {
            const result: Record<string, boolean> = {};
            let all = true;
            let any = false;

            keys.forEach((key) => {
                all =
                    all &&
                    state[reducer] &&
                    state[reducer][key] &&
                    state[reducer][key].loaded;
                any =
                    any ||
                    (state[reducer] && state[reducer][key] && state[reducer][key].loaded);
                result[key] =
                    state[reducer] && state[reducer][key] && state[reducer][key].loaded;
            });

            result.all = all;
            result.any = any;

            return result;
        },

        getState(state: Record<string, Record<string, AsyncState>>, ...keys: string[]) {
            const result: Record<string, number> = {};

            keys.forEach((key) => {
                if (!state[reducer] || !state[reducer][key]) {
                    result[key] = STATE_UNKNOWN;
                } else {
                    const value = state[reducer][key];
                    if (value.error) {
                        result[key] = STATE_ERROR;
                    } else if (value.loading) {
                        result[key] = STATE_LOADING;
                    } else if (value.loaded) {
                        result[key] = STATE_READY;
                    } else {
                        result[key] = STATE_UNLOADED;
                    }
                }
            });

            return result;
        },

        hasErrors(state: Record<string, Record<string, AsyncState>>, ...keys: string[]) {
            const result: Record<string, boolean> = {};
            let all = true;
            let any = false;

            keys.forEach((key) => {
                all =
                    all &&
                    state[reducer] &&
                    state[reducer][key] &&
                    state[reducer][key].error !== null;
                any =
                    any ||
                    (state[reducer] &&
                        state[reducer][key] &&
                        state[reducer][key].error !== null);
                result[key] =
                    state[reducer] &&
                    state[reducer][key] &&
                    state[reducer][key].error !== null;
            });

            result.all = all;
            result.any = any;

            return result;
        },

        getErrors(state: Record<string, Record<string, AsyncState>>, ...keys: string[]) {
            const result: Record<string, string | null> = {};

            keys.forEach((key) => {
                if (state[reducer][key].error !== null) {
                    result[key] =
                        state[reducer] &&
                        state[reducer][key] &&
                        state[reducer][key].error;
                }
            });

            return result;
        },
    };
}

export const defaultOptions: RequestOptions = { headers: {} };

export function authHeaders(
    securityHelper: SecurityUtils,
    addlHeaders: Record<string, string> = {}
) {
    return { ...addlHeaders, Authorization: securityHelper.getAuthHeader() };
}

export const apiHelpers = (securityHelper: SecurityUtils) => {
    const Get = (url: string, options: RequestOptions = defaultOptions) =>
        axios.get(url, options);
    const post = (url: string, data: unknown, options: RequestOptions = defaultOptions) =>
        axios.post(url, data, options);
    const put = (url: string, data: unknown, options: RequestOptions = defaultOptions) =>
        axios.put(url, data, options);
    const remove = (url: string, options: RequestOptions = defaultOptions) =>
        axios.delete(url, options);

    return {
        get: Get,
        post,
        put,
        remove,
        getWithAuth(url: string, options: RequestOptions = defaultOptions) {
            return Get(url, {
                ...options,
                headers: authHeaders(securityHelper, options.headers),
            });
        },
        postWithAuth(
            url: string,
            data: unknown,
            options: RequestOptions = defaultOptions
        ) {
            return post(url, data, {
                ...options,
                headers: authHeaders(securityHelper, options.headers),
            });
        },
        putWithAuth(
            url: string,
            data: unknown,
            options: RequestOptions = defaultOptions
        ) {
            return put(url, data, {
                ...options,
                headers: authHeaders(securityHelper, options.headers),
            });
        },
        removeWithAuth(url: string, options: RequestOptions = defaultOptions) {
            return remove(url, {
                ...options,
                headers: authHeaders(securityHelper, options.headers),
            });
        },
    };
};
